import React, {
  RefObject,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Loader } from "semantic-ui-react";

import AddBoxIcon from "@mui/icons-material/AddBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { TreeView } from "@mui/x-tree-view/TreeView";

// Types
export type TreeViewSearchBoxProps = {
  id?: number | string;
  error?: any;
  fixPosition?: boolean;
  items?: TreeNode[];
  placeholder?: string;
  // config
  disabled?: boolean;
  value?: any;
  onChange?: (data: { id?: number | string; text: string; value: any }) => any;
  onParentSearch?: (data: {
    abortController: RefObject<AbortController | null>;
    id?: number | string;
    parent: string;
  }) => void;
  // callback
  onSearch?: (data: {
    abortController: RefObject<AbortController | null>;
    id?: number | string;
    search: string;
  }) => void;
};

export type TreeNode = {
  id: number | string;
  [key: string]: any;
  children?: TreeNode[]; // Recursive definition
  name: string;
};

const DEFAULT_EXPANDED = ["root"];

const prepareNodesWithLoader = (items: TreeNode[]) =>
  items.map((item): TreeNode => {
    const formattedItem = { ...item };

    // ถ้าเป็น parent แต่ไม่มี children ให้เพิ่ม loading placeholder
    if (item.has_children && !item.children) {
      formattedItem.children = [{ id: `loading-${item.id}`, name: "" }];
    }
    // ถ้ามี children อยู่แล้ว ให้ทำ recursive เพื่อจัดการกับ children
    else if (Array.isArray(item.children)) {
      formattedItem.children = prepareNodesWithLoader(item.children);
    }

    return formattedItem;
  });

const TreeViewSearchBox = (props: TreeViewSearchBoxProps) => {
  const [searchText, setSearchText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [treeViewData, setTreeViewData] = useState<any>({});

  const [toggleList, setToggleList] = useState<any[]>([]);
  const [selected, setSelected] = useState<number | string>("");

  // Ref
  const timeoutRef = useRef<any>(0);
  const abortController = useRef<AbortController>(null);

  // Callback Effect
  const getOptionsName = useCallback(
    (nodes: TreeNode, selectedValue: TreeNode["id"]): string | undefined => {
      if (nodes.id === selectedValue) {
        return nodes.name;
      }

      const children = nodes.children ?? [];

      if (children.length > 0) {
        for (const node of children) {
          const result = getOptionsName(node, selectedValue);

          if (result !== undefined) {
            return result;
          }
        }
      }

      return undefined;
    },
    []
  );

  const handleClear = useCallback(() => {
    // * หาก มี การ request อยู่แต่มีการแก้ไขคำค้นหาให้ Cancel request
    if (abortController.current) {
      abortController.current.abort();
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const handleChange = useCallback(
    (selectedValue: any) => {
      const optionsName = getOptionsName(treeViewData, selectedValue);

      if (!optionsName) {
        return;
      }

      (document.querySelector(`#tree-view-${props.id}`) as HTMLDivElement | null)?.blur();

      setSearchText("");
      setSelected("");
      setToggleList([]);
      setIsLoading(false);

      handleClear();

      props.onChange?.({ id: props.id, text: optionsName, value: selected });
    },
    [props.id, selected, treeViewData]
  );

  // Effect
  useEffect(() => {
    console.log("[Use effect] Tree view data:", props.items);

    setIsLoading(false);

    setTreeViewData({ id: "root", children: prepareNodesWithLoader(props.items ?? []), name: "" });
  }, [props.items]);

  useEffect(() => {
    setSearchText(props.value);
  }, [props.value]);

  useEffect(() => {
    if (!selected) {
      // ---
    } else if (toggleList.includes(selected)) {
      // ---
    } else {
      handleChange(selected);
    }
  }, [selected]);

  // Memo
  const sx = useMemo(
    () => ({
      "&.Mui-focused fieldset.MuiOutlinedInput-notchedOutline": {
        borderColor: "#96c8da",
        borderWidth: 1,
      },
      "& .MuiOutlinedInput-root:hover fieldset": { borderColor: "rgba(34,36,38,.35)" },
      "& ~ div": props.fixPosition
        ? {
            transform: "translate(0px, 37px) !important",
          }
        : {},
      background: props.disabled ? "rgba(255,255,255,0.38)" : "white",
    }),
    [props.disabled, props.fixPosition]
  );

  const popupIcon = useMemo(
    () =>
      isLoading ? (
        <div style={{ marginLeft: "4px", paddingTop: "4px" }}>
          <CircularProgress color="inherit" size={12} />
        </div>
      ) : undefined,
    [isLoading]
  );

  const options = useMemo(
    () => [props.value || searchText || ""] as string[],
    [props.value, searchText]
  );

  // Handler
  const renderTree = useCallback(
    (nodes: TreeNode) => {
      const nodeId = nodes.id.toString();

      return (
        <TreeItem
          key={nodeId}
          nodeId={nodes.id as string}
          label={
            nodeId.includes("loading") ? (
              <TreeItemLoading
                id={props.id}
                nodeId={nodeId}
                onParentSearch={props.onParentSearch}
              />
            ) : (
              nodes.name
            )
          }
          sx={{
            ...(nodes.id === "root" && {
              "&": { marginLeft: "-17px" },
              "& > div": { display: "none" },
            }),
            "& .MuiCollapse-root:not(:has(.MuiSvgIcon-root,.loader)) .MuiTreeItem-iconContainer": {
              marginLeft: "-14px",
            },
            // เพิ่ม styles สำหรับ indent lines
            "& .MuiTreeItem-content .MuiTreeItem-label": {
              fontSize: "0.875rem",
              fontWeight: 400,
              padding: "3px 0",
            },
            // Add connector lines styling
            "& .MuiTreeItem-group": {
              borderLeft: "1px solid #e0e0e0",
              marginLeft: "16px",
              paddingLeft: "8px",
              position: "relative",
            },
            // For the first direct TreeItem in a group that has children
            "& .MuiTreeItem-group > .MuiTreeItem-root:not(:last-child)::after": {
              borderLeft: "1px solid #e0e0e0",
              bottom: "0",
              height: "100%",
              left: "-8px",
              position: "absolute",
              content: '""',
            },
            // Add a horizontal connector line before each TreeItem
            "& .MuiTreeItem-root::before": {
              backgroundColor: "#e0e0e0",
              display: nodes.id === "root" ? "none" : "block",
              height: "1px",
              left: "-8px",
              position: "absolute",
              top: "11px",
              width: "8px",
              content: '""',
            },
          }}
        >
          {Array.isArray(nodes.children) ? nodes.children.map((node) => renderTree(node)) : null}
        </TreeItem>
      );
    },
    [props.id]
  );

  const handleToggle = useCallback(
    (event: SyntheticEvent, nodeIds: any[]) => {
      const target = event.target as HTMLElement;
      const isLabel = target.getAttribute("class")?.includes("label");

      const collapseId = toggleList.find((id) => !nodeIds.includes(id));

      if (collapseId && isLabel) {
        handleChange(collapseId);
      } else {
        setToggleList(nodeIds);
      }
    },
    [toggleList]
  );

  const handleSelect = useCallback(
    (event: SyntheticEvent, nodeId: number | string) => {
      const target = event.target as HTMLElement;
      const isLabel = target.getAttribute("class")?.includes("label");

      const isCollapse = toggleList.includes(nodeId);

      if (isCollapse && !isLabel) {
        setSelected("");
      } else {
        setSelected(nodeId);
      }
    },
    [toggleList]
  );

  const handleInputChange = useCallback(
    (event: SyntheticEvent, value: string, reason: string) => {
      console.log("handleInputChange called", { reason, value });
      handleClear();

      if (reason !== "input") {
        return;
      }

      const isSearch = value.length === 0 || value.length > 2;

      console.log("isSearch:", isSearch);

      setIsLoading(isSearch);
      setSearchText(value);

      if (isSearch) {
        timeoutRef.current = setTimeout(() => {
          console.log("Setting timeout", { id: props.id, abortController, search: value });

          props.onSearch?.({ id: props.id, abortController, search: value });
        }, 300);
      }
    },
    [handleClear, props.id]
  );

  return (
    <Autocomplete
      key={`tree-view-box-${props.id}`}
      id={`tree-view-${props.id}`}
      disabled={props.disabled}
      inputValue={searchText || props.value || ""}
      // input
      popupIcon={popupIcon}
      size="small"
      sx={sx}
      disablePortal
      // options
      options={options}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          error={props.error}
          {...params}
          placeholder={props.placeholder}
          variant="outlined"
          inputProps={{
            ...params.inputProps,
            title: searchText || props.value || props.placeholder,
          }}
          sx={{
            "& input[type=text].MuiInputBase-input": {
              border: "none",
            },
          }}
        />
      )}
      renderOption={() => (
        <div style={{ overflow: "hidden", width: "100%" }}>
          <TreeView
            aria-label="rich object"
            defaultCollapseIcon={<IndeterminateCheckBoxIcon />}
            defaultExpanded={DEFAULT_EXPANDED}
            defaultExpandIcon={<AddBoxIcon />}
            onNodeSelect={handleSelect}
            onNodeToggle={handleToggle}
            sx={{
              // TreeView container styles
              "& .MuiTreeItem-root": {
                marginBottom: "2px",
                position: "relative",
              },
              // Global TreeView styles
              "& .MuiTreeView-root": {
                padding: "4px 0",
              },
              flexGrow: 1,
              overflowY: "auto",
            }}
          >
            {renderTree(treeViewData)}
          </TreeView>
        </div>
      )}
    />
  );
};

/* ====================================================== */
/*                    TreeItemLoading;                    */
/* ====================================================== */
type TreeItemLoadingProps = Pick<TreeViewSearchBoxProps, "id" | "onParentSearch"> & {
  nodeId: string;
};

const TreeItemLoading = (props: TreeItemLoadingProps) => {
  const abortController = useRef<AbortController>(null);

  useEffect(() => {
    const [, id] = props.nodeId.split("-");

    props.onParentSearch?.({ id: props.id, abortController, parent: id });

    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, []);

  return (
    <div>
      <Loader style={{ left: "-17px" }} size="mini" active />
      {"\u00A0"}
    </div>
  );
};

TreeViewSearchBox.displayName = "TreeViewSearchBox";

export default React.memo(TreeViewSearchBox);
